/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useContext } from 'react';
import { Axis, BlueprintInstanceType, COUNTERTOP_TEXTURE_DIMENSION, IslandType, Vertex3 } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DimensionLine } from '@r-components';
import { DisplayMode } from '@r-core';
import { RenderBreakpointsCountertop } from './render-breakpoints-countertop';
import { RenderCountertopMask } from './render-countertop-mask';
import { renderTracking } from '@r-services/render-tracking';
import { showCountertopDil } from '../show/show-countertop.dil';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { usePanel } from '@r-hooks';
import { AddIslandActions, IslandActions } from '../../components/action-menu';
import { convertPolyPoints2Vectors, convertRect2Vectors } from '@r-core/helpers';
import { Layer, ShowDimensions } from '@r-core/types';

import '../../app/styles/blueprint-component.scss';
import { RenderOverheadPanelItemGroups } from './render-overhead-panel-item-groups';

export type RenderObstructionOverheadProps = {
	islandId: string;
	offsetX?: number;
	offsetY?: number;
};

export const RenderObstructionOverhead = (props: RenderObstructionOverheadProps): React.ReactElement => {
	const {
		controller: { options, kitchen, navigation }
	} = useContext(AppContext);

	const kitchenSize = kitchen.getSize(options.showCountertops);
	const { islandId, offsetX = 0, offsetY = 0 } = props;
	const island = kitchen.design.obstructions.findById(islandId);

	if (!island) {
		console.warn('RenderObstructionOverhead: Obstruction not found', islandId);
		return <g key={`${islandId}-overhead-not-found`} />;
	}

	const materialAsset = kitchen.material.countertop;
	const materialScale = {
		height: materialAsset.scale.height * COUNTERTOP_TEXTURE_DIMENSION,
		width: materialAsset.scale.width * COUNTERTOP_TEXTURE_DIMENSION
	};

	const blueprintToApplianceOpacity = (100 - options.applianceOpacity) / 100;

	const panel = island.frontPanel;

	if (!panel) {
		console.warn('RenderObstructionOverhead: No front panel found for obstruction', islandId);
		return <g key={`${islandId}-overhead-empty`} />;
	}

	const shouldRenderPanel = (() => {
		const { material } = island.refMap.kitchen;

		if (!material) {
			return true;
		}

		if ('viewType' in material && material.viewType !== undefined) {
			return material.viewType !== 'bottom' && material.viewType !== 'bottomOnly';
		}
		if ('bottomOnly' in material && material.bottomOnly !== undefined) {
			return !material.bottomOnly;
		}
		if ('viewTypes' in material && material.viewTypes !== undefined && Array.isArray(material.viewTypes)) {
			return material.viewTypes.includes('overhead') || material.viewTypes.includes('top');
		}
		return true;
	})();

	const panelVectors = convertRect2Vectors({
		layer: Layer.Panel,
		center: { x: 0, y: 0 },
		width: panel.size.width,
		height: panel.thickness,
		angle: 0
	});

	const countertop = island.getCountertop();
	const renderDetails = countertop?.renderingDetails;
	const countertopVectors = renderDetails ? convertPolyPoints2Vectors(renderDetails) : [];
	const translate = {
		x: offsetX,
		y: offsetY
	};

	let showTexture = true;
	let includeLabelBox = true;
	let useDimensionBox = true;
	let { showAppliances } = options;
	const { showCutouts, showCorners } = options
	const { showPanelOutsideDimensions, showCountertops } = options;

	switch (navigation.displayMode) {
		case DisplayMode.OverheadManufacturing:
		case DisplayMode.SkuOverheadManufacturing:
			showTexture = false;
			showAppliances = false;
			includeLabelBox = false;
			useDimensionBox = false;
			break;
		default:
	}

	// Obstructions have a simplified panel structure that does not support
	// intersection area calculations — skip them to avoid noisy console warnings.
	const intersectionAreas = [];

	renderTracking.incrementRender('ShowOverhead');
	return (<g key={`${islandId}-overhead`} transform={`translate(${translate.x}, ${translate.y})`}>
		{ showCountertops && countertop && <>
			{ /* Create countertop texture def */ }
			{ showTexture && <defs>
				<pattern id="counter-texture" x="0" y="0" height={materialScale.height} width={materialScale.width} patternUnits="userSpaceOnUse">
					<image href={`${options.assetsBaseUrl}/textures/${materialAsset.texture}${options.assetsSasToken}`} x="0" y="0" width={materialScale.width} height={materialScale.height} />
				</pattern>
			</defs>}

			{ /* create cutout mask for countertop */ }
			{(showCutouts) &&
				<RenderCountertopMask islandId={islandId} />
			}

			{ /* Draw countertop */ }
			{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				fill: 'darkgray',
				stroke: 'lightgrey',
				strokeWidth: '.1',
				opacity: blueprintToApplianceOpacity === 0 ? 1 : 0.65,
				mask: `url(#countertop-mask-${islandId})`,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: countertop.id })
			})}
		</> }

		{ /* Draw hidden countertop layer for context menu */ }
		{ !showCountertops && countertop && <>
			{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				opacity: 0,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: countertop.id })
			})}
		</> }

		{ /* Draw Countertop Texture */ }
		{ showCountertops && showTexture && countertop &&
			<>{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop',
				stroke: 'none',
				fill: 'url(#counter-texture)',
				mask: `url(#countertop-mask-${islandId})`,
				opacity: blueprintToApplianceOpacity,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: countertop.id })
			})}</>
		}


		{ /* Render panel */ }
		{ shouldRenderPanel && <g
			key={panel.id}
			type="island-panel"
			transform={`translate(${panel.center.x}, ${panel.center.z}) rotate(${-panel.getOriginAngle()})`}
		>
			{ svgPolygonFromVectors(
				panelVectors,
				{
					key: panel.id,
					fill: '#7CA9BD',
					stroke: 'black',
					strokeWidth: '.0625',
					opacity: options.showCountertops ? 0.25 : 0.75,
					...addContextMenu({ menuKey: BlueprintInstanceType.Obstruction, id: island.id })
				}
			) }

			{ /* display Appliances */}
			{(showAppliances || showCutouts) &&
				<RenderOverheadPanelItemGroups panelItemGroups={panel.panelItemGroups} />
			}
		</g> }
	</g>);
};
